import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../hooks';
import { selectUser, selectAuthLoading } from '../store/slices/authSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import apiClient from '../services/api';
import { SubscriptionPlan } from '../types/api';
import { Crown, BookOpen, Bot, Check, Rocket, Plus, Info, Shield, CreditCard } from 'lucide-react';
import './Subscription.css';

const Subscription: React.FC = () => {
  const user = useAppSelector(selectUser);
  const authLoading = useAppSelector(selectAuthLoading);

  const [isLoading, setIsLoading] = useState(true);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  useEffect(() => {
    console.log('Subscription page - authLoading:', authLoading, 'user:', user);
    // Only try to load plans if we have a user and auth is not loading
    if (!authLoading && user) {
      console.log('Loading subscription plans...');
      loadPlans();
    }
  }, [user, authLoading]);

  const loadPlans = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const plansData = await apiClient.getActiveSubscriptionPlans();
      // Sort plans by price
      const sortedPlans = plansData.sort((a, b) =>
        (a.price_monthly || 0) - (b.price_monthly || 0)
      );
      setPlans(sortedPlans);
    } catch (error: any) {
      console.error('Error loading plans:', error);
      setError('Failed to load subscription plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradeClick = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setShowConfirmModal(true);
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan || !user) return;

    try {
      setActionLoading('upgrade');

      // Create Stripe Checkout Session
      const { url } = await apiClient.createStripeCheckoutSession(
        selectedPlan.plan_name,
        `${window.location.origin}/subscription/success`,
        `${window.location.origin}/subscription/cancel`
      );

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      setError(error.response?.data?.detail || 'Failed to start checkout process');
      setActionLoading(null);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setSelectedPlan(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const isCurrentPlan = (planName: string) => {
    return user?.subscription_tier?.toLowerCase() === planName.toLowerCase();
  };

  const canUpgradeTo = (plan: SubscriptionPlan) => {
    if (!user) return false;

    // Add-ons can always be purchased (if not on Free tier)
    if (plan.is_addon) {
      const currentTier = user.subscription_tier?.toLowerCase() || 'free';
      return currentTier !== 'free';
    }

    const currentTier = user.subscription_tier?.toLowerCase() || 'free';
    const planTier = plan.plan_name.toLowerCase();

    // Define tier hierarchy
    const tierOrder: { [key: string]: number } = {
      'free': 0,
      'pro': 1,
      'premium': 2
    };

    return tierOrder[planTier] > (tierOrder[currentTier] || 0);
  };

  const getPlanFeatures = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'free':
        return [
          '3 chapters per memoir',
          '50 AI requests/month',
          'Basic export formats (PDF, DOCX)',
          'Email support'
        ];
      case 'pro':
        return [
          'Unlimited chapters',
          '1,000 AI requests/month',
          'Advanced export options',
          'Priority email support',
          'Version history',
          'Advanced formatting'
        ];
      case 'premium':
        return [
          'Unlimited chapters',
          'Unlimited AI requests',
          'All export formats',
          'Priority support + live chat',
          'Full version history',
          'Collaboration features',
          'Custom themes',
          'API access'
        ];
      case 'ai credits':
        return [
          '+1,000 additional AI requests/month',
          'Stacks with your current plan',
          'Renews monthly',
          'Cancel anytime'
        ];
      default:
        return [];
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="subscription-page">
        <div className="subscription-container">
          <LoadingSpinner size="lg" text="Loading subscription plans..." />
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="subscription-container">
        <header className="subscription-header">
          <Link to="/dashboard" className="back-link">
            ← Back to Dashboard
          </Link>
        </header>

        {error && (
          <div className="alert alert-error">
            {error}
            <button className="alert-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {user && (
          <div className="current-plan-banner">
            <div className="banner-content">
              <div className="banner-icon"><Crown size={32} /></div>
              <div className="banner-text">
                <h3>Current Plan: <span className="current-plan-name">
                  {(user.subscription_tier || 'free').charAt(0).toUpperCase() + (user.subscription_tier || 'free').slice(1)}
                </span></h3>
                <p>
                  {user.chapter_limit === -1 ? 'Unlimited chapters' : `${user.chapter_limit} chapters`} • {' '}
                  {user.ai_usage_limit === -1 ? 'Unlimited AI requests' : `${user.ai_usage_limit} AI requests/month`}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="plans-grid-user">
          {plans.map(plan => (
            <div
              key={plan.plan_id}
              className={`plan-card-user ${isCurrentPlan(plan.plan_name) ? 'current' : ''}`}
            >
              {isCurrentPlan(plan.plan_name) && (
                <div className="current-badge">
                  Current Plan
                </div>
              )}

              <div className="plan-header-user">
                <h2 className="plan-name-user">
                  {plan.plan_name.charAt(0).toUpperCase() + plan.plan_name.slice(1)}
                </h2>
                <div className="plan-pricing-user">
                  <div className="price-main">
                    <span className="price-amount">{formatCurrency(plan.price_monthly || 0)}</span>
                    <span className="price-period">/month</span>
                  </div>
                  {plan.price_yearly && plan.price_yearly > 0 && (
                    <div className="price-yearly">
                      or {formatCurrency(plan.price_yearly || 0)}/year
                      <span className="savings-badge">Save {Math.round((1 - (plan.price_yearly / 12) / plan.price_monthly) * 100)}%</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="plan-limits-user">
                <div className="limit-item-user">
                  <span className="limit-icon"><BookOpen size={20} /></span>
                  <span className="limit-text">
                    {plan.chapter_limit === -1 ? 'Unlimited chapters' : `${plan.chapter_limit} chapters`}
                  </span>
                </div>
                <div className="limit-item-user">
                  <span className="limit-icon"><Bot size={20} /></span>
                  <span className="limit-text">
                    {plan.ai_usage_limit === -1 ? 'Unlimited AI requests' : `${plan.ai_usage_limit} AI requests/month`}
                  </span>
                </div>
              </div>

              <div className="plan-features-user">
                <h4>Features included:</h4>
                <ul>
                  {getPlanFeatures(plan.plan_name).map((feature, index) => (
                    <li key={index}>
                      <span className="feature-check"><Check size={16} /></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="plan-action-user">
                {isCurrentPlan(plan.plan_name) ? (
                  <button className="btn btn-current" disabled>
                    Current Plan
                  </button>
                ) : canUpgradeTo(plan) ? (
                  <button
                    className="btn btn-upgrade"
                    onClick={() => handleUpgradeClick(plan)}
                  >
                    {plan.is_addon ? `Add ${plan.plan_name}` : `Upgrade to ${plan.plan_name.charAt(0).toUpperCase() + plan.plan_name.slice(1)}`}
                  </button>
                ) : (
                  <button className="btn btn-downgrade" disabled>
                    {plan.is_addon ? 'Not Available' : 'Lower Plan'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="subscription-footer">
          <div className="footer-note">
            <p><Shield size={16} className="footer-icon" /> All plans include a 14-day money-back guarantee</p>
            <p><CreditCard size={16} className="footer-icon" /> Secure payment processing • Cancel anytime • No hidden fees</p>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedPlan && (
        <div className="modal-overlay" onClick={handleCancelConfirm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedPlan.is_addon ? 'Add Plan Add-on' : 'Confirm Subscription Upgrade'}</h2>
              <button
                className="close-btn"
                onClick={handleCancelConfirm}
                disabled={!!actionLoading}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="confirm-upgrade-content">
                <div className="upgrade-icon-large">{selectedPlan.is_addon ? <Plus size={48} /> : <Rocket size={48} />}</div>
                <p className="confirm-text">
                  {selectedPlan.is_addon
                    ? `You're about to add the <strong>${selectedPlan.plan_name}</strong> add-on to your current plan.`
                    : `You're about to upgrade to the <strong>${selectedPlan.plan_name.charAt(0).toUpperCase() + selectedPlan.plan_name.slice(1)}</strong> plan.`
                  }
                </p>
                <div className="upgrade-details">
                  <div className="detail-item">
                    <span className="detail-label">Monthly Price:</span>
                    <span className="detail-value">{formatCurrency(selectedPlan.price_monthly || 0)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Chapter Limit:</span>
                    <span className="detail-value">
                      {selectedPlan.chapter_limit === -1 ? 'Unlimited' : selectedPlan.chapter_limit}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">AI Usage:</span>
                    <span className="detail-value">
                      {selectedPlan.ai_usage_limit === -1 ? 'Unlimited' : `${selectedPlan.ai_usage_limit}/month`}
                    </span>
                  </div>
                </div>
                <p className="confirm-note">
                  <Info size={16} className="info-icon" /> You will be redirected to Stripe's secure payment page to complete your subscription.
                </p>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelConfirm}
                disabled={!!actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmUpgrade}
                disabled={!!actionLoading}
              >
                {actionLoading === 'upgrade' ? <LoadingSpinner size="sm" color="white" /> : (selectedPlan.is_addon ? 'Add to Plan' : 'Confirm Upgrade')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
